import {skipToken} from '@reduxjs/toolkit/query'
import * as React from 'react'
import {memo, Suspense} from 'react'
import {EntryPointContainer} from 'react-relay'
import type {PreloadedEntryPoint} from 'react-relay/relay-hooks/EntryPointTypes'

import {useQueryParam} from '../../../../hooks/routes'
import {getSingleBuildArg} from '../../../../rest/builds'
import {getBuildTypesArg, getDependantsLocator} from '../../../../rest/buildTypes'
import {getBuildLocator} from '../../../../rest/locators'
import {getBuild} from '../../../../selectors'
import {restApi} from '../../../../services/rest'
import type {BuildId} from '../../../../types'
import Loader from '../../../common/Loader/Loader'
import {useMode} from '../../../common/ModeSwitch/ModeSwitch'

import type {DependenciesTabComponent} from './DependenciesTab'
import {DependenciesTypes, SnapshotDependenciesModes} from './DependenciesTab.modes'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = {
  buildId: BuildId
  entryPoint?: PreloadedEntryPoint<DependenciesTabComponent>
}

function DependenciesContainer({buildId, entryPoint}: Props) {
  const type = useQueryParam('type', DependenciesTypes.SNAPSHOT)
  const isSnapshot = type === DependenciesTypes.SNAPSHOT
  const {isSnapshotDependenciesLoaded} = restApi.endpoints.getBuildNormalized.useQuery(
    getSingleBuildArg(getBuildLocator(buildId), {withSnapshotDependencies: true}),
    {
      skip: !isSnapshot,
      selectFromResult: ({data}) => ({isSnapshotDependenciesLoaded: data != null}),
    },
  )
  const mode = useMode()
  const isChain = isSnapshot && mode === SnapshotDependenciesModes.CHAIN
  const buildTypeId = useAppSelector(state => getBuild(state, buildId)?.buildType)
  restApi.endpoints.getAllBuildTypesNormalized.useQuerySubscription(
    isChain && buildTypeId != null
      ? getBuildTypesArg(getDependantsLocator(buildTypeId), {withSnapshotDependencies: true})
      : skipToken,
  )
  return (isChain || isSnapshotDependenciesLoaded || !isSnapshot) && entryPoint != null ? (
    <Suspense fallback={<Loader />}>
      <EntryPointContainer entryPointReference={entryPoint} props={{buildId}} />
    </Suspense>
  ) : (
    <Loader />
  )
}

export default memo(DependenciesContainer)
